import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Mail, Pause, Play, Plus, RotateCw, Trash2, UserPlus, X } from 'lucide-react';
import { useToast } from '@/components/common/Toast';
import ums, { assignableRoles, COMPANY_ADMIN_ROLE, takesSeat, UmsMember, UmsRole } from '@/services/ums.service';

/**
 * The people of one company and their roles, kept in GeoNexora's user management service. The same screen
 * for the company's own administrator (Users, from the map) and for our staff (Companies → Users): UMS
 * decides what each of them may change, and a refusal is shown as it says it.
 *
 * Roles here are for the whole company. Workspace roles come from sharing a workspace and are only counted.
 */

const input = 'w-full px-3 py-2 border border-gn-light rounded bg-gn-surface text-gn-primary';
const button = 'inline-flex items-center gap-1 px-3 py-2 border border-gn-light rounded hover:bg-gn-tertiary disabled:opacity-50 text-sm';

interface Props {
  tenantId: string;
  /** The company's seats, when known (staff see it; a company admin may not). */
  seatLimit?: number | null;
}

const CompanyUsers: React.FC<Props> = ({ tenantId, seatLimit }) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [inviteRoles, setInviteRoles] = useState<string[]>([]);
  const [filter, setFilter] = useState('');

  const membersKey = ['umsMembers', tenantId];
  const members = useQuery({ queryKey: membersKey, queryFn: () => ums.members(tenantId), retry: false });
  const roles = useQuery({ queryKey: ['umsRoles', tenantId], queryFn: () => ums.roles(tenantId), retry: false });
  const offered = useMemo(() => assignableRoles(roles.data ?? []), [roles.data]);
  const roleById = useMemo(() => new Map((roles.data ?? []).map((r) => [r.id, r])), [roles.data]);

  const used = (members.data ?? []).filter(takesSeat).length;
  const shown = (members.data ?? []).filter((m) => {
    const f = filter.trim().toLowerCase();
    return !f || `${m.displayName} ${m.email ?? ''}`.toLowerCase().includes(f);
  });

  const done = (message: string) => {
    queryClient.invalidateQueries({ queryKey: membersKey });
    addToast(message, 'success');
  };
  const failed = (e: unknown) => addToast(e instanceof Error ? e.message : String(e), 'error');

  const invite = useMutation({
    mutationFn: () => ums.invite(tenantId, { email: email.trim(), displayName: name.trim(), locale: (i18n.language || 'en').substring(0, 2) }, inviteRoles),
    onSuccess: () => {
      done(t('access.invited', { email: email.trim() }));
      setInviting(false);
      setEmail('');
      setName('');
      setInviteRoles([]);
    },
    onError: failed,
  });

  const act = useMutation({
    mutationFn: async (a: { kind: MemberAction; member: UmsMember; id?: string }) => {
      switch (a.kind) {
        case 'assign': return ums.assign(tenantId, a.member.principalId, a.id!);
        case 'revoke': return ums.revoke(tenantId, a.id!);
        case 'suspend': return ums.suspendMember(tenantId, a.member.principalId);
        case 'reactivate': return ums.reactivateMember(tenantId, a.member.principalId);
        case 'remove': return ums.removeMember(tenantId, a.member.principalId);
        case 'resend': return ums.resendInvitation(tenantId, a.member.principalId);
      }
    },
    onSuccess: (_, a) => done(a.kind === 'resend' ? t('access.invitation_resent', { email: a.member.email ?? '' }) : t('access.changed')),
    onError: failed,
  });

  const roleName = (key: string, fallback: string) => t(`access.role.${key}`, { defaultValue: fallback });

  if (members.isError) {
    return <p className="text-sm text-gn-danger">{(members.error as Error).message}</p>;
  }

  const seats = seatLimit == null
    ? t('access.seats_used', { used })
    : t('access.seats_of', { used, limit: seatLimit });
  const full = seatLimit != null && used >= seatLimit;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className={`text-sm px-2 py-1 rounded ${full ? 'bg-gn-danger-light text-gn-danger' : 'bg-gn-surface'}`}>{seats}</span>
        <input className={`${input} max-w-xs`} placeholder={t('access.search')} value={filter} onChange={(e) => setFilter(e.target.value)} />
        <div className="flex-1" />
        <button type="button" className="inline-flex items-center gap-2 px-4 py-2 bg-gn-blue text-gn-inverse rounded text-sm"
                onClick={() => setInviting(true)}>
          <UserPlus size={16} /> {t('access.invite')}
        </button>
      </div>

      {inviting && (
        <div className="border border-gn-light rounded p-4 mb-4 bg-gn-surface">
          <div className="flex justify-between items-center mb-3">
            <b>{t('access.invite_title')}</b>
            <button type="button" onClick={() => setInviting(false)} aria-label={t('buttons.cancel')}><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="block text-sm">{t('access.email')}
              <input className={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="block text-sm">{t('access.name')}
              <input className={input} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
          </div>
          <p className="text-sm mb-1">{t('access.roles')}</p>
          <div className="flex flex-wrap gap-3 mb-3">
            {offered.map((r) => (
              <label key={r.id} className="inline-flex items-center gap-1 text-sm">
                <input type="checkbox" checked={inviteRoles.includes(r.id)}
                       onChange={(e) => setInviteRoles(e.target.checked ? [...inviteRoles, r.id] : inviteRoles.filter((x) => x !== r.id))} />
                {roleName(r.key, r.name)}
              </label>
            ))}
          </div>
          <p className="text-xs text-gn-muted mb-3">{t('access.invite_hint')}</p>
          <button type="button" className="inline-flex items-center gap-2 px-4 py-2 bg-gn-blue text-gn-inverse rounded text-sm disabled:opacity-50"
                  disabled={!email.includes('@') || !name.trim() || inviteRoles.length === 0 || invite.isPending}
                  onClick={() => invite.mutate()}>
            <Mail size={16} /> {t('access.send_invitation')}
          </button>
        </div>
      )}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gn-muted border-b border-gn-light">
            <th className="py-2 pr-2">{t('access.name')}</th>
            <th className="py-2 pr-2">{t('access.state')}</th>
            <th className="py-2 pr-2">{t('access.roles')}</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {members.isLoading && <tr><td colSpan={4} className="py-3 text-gn-muted">…</td></tr>}
          {shown.map((m) => (
            <MemberRow key={m.principalId} member={m} offered={offered} roleById={roleById} tenantId={tenantId}
                       roleName={roleName} busy={act.isPending}
                       onAct={(kind, id) => act.mutate({ kind, member: m, id })} />
          ))}
          {!members.isLoading && shown.length === 0 && (
            <tr><td colSpan={4} className="py-3 text-gn-muted">{t('access.nobody')}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

type MemberAction = 'assign' | 'revoke' | 'suspend' | 'reactivate' | 'remove' | 'resend';

interface RowProps {
  member: UmsMember;
  offered: UmsRole[];
  roleById: Map<string, UmsRole>;
  tenantId: string;
  roleName: (key: string, fallback: string) => string;
  busy: boolean;
  onAct: (kind: MemberAction, id?: string) => void;
}

const MemberRow: React.FC<RowProps> = ({ member: m, offered, tenantId, roleName, busy, onAct }) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState('');
  const atCompany = m.assignments.filter((a) => a.scopeId === tenantId);
  const workspaces = new Set(m.assignments.filter((a) => a.scopeId !== tenantId).map((a) => a.scopeId)).size;
  const held = new Set(atCompany.map((a) => a.roleId));
  const canAdd = offered.filter((r) => !held.has(r.id));
  const state = m.state === 'INVITED' ? t('access.state_invited') : m.state === 'SUSPENDED' ? t('access.state_suspended') : t('access.state_active');

  return (
    <tr className={`border-b border-gn-light align-top ${m.state === 'SUSPENDED' ? 'opacity-60' : ''}`}>
      <td className="py-2 pr-2">
        <div className="font-medium flex items-center gap-2">
          {m.displayName}
          {m.staff && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gn-blue text-gn-inverse" title={t('staff.badge_hint')}>
              {t('staff.badge')}
            </span>
          )}
        </div>
        <div className="text-xs text-gn-muted">{m.email}</div>
      </td>
      <td className="py-2 pr-2 whitespace-nowrap">{state}</td>
      <td className="py-2 pr-2">
        <div className="flex flex-wrap gap-1 items-center">
          {atCompany.map((a) => (
            <span key={a.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${a.roleKey === COMPANY_ADMIN_ROLE ? 'bg-gn-blue text-gn-inverse' : 'bg-gn-surface border border-gn-light'}`}>
              {roleName(a.roleKey, a.roleName)}
              <button type="button" disabled={busy} title={t('access.take_role')} onClick={() => onAct('revoke', a.id)}><X size={12} /></button>
            </span>
          ))}
          {workspaces > 0 && <span className="text-xs text-gn-muted">{t('access.in_workspaces', { count: workspaces })}</span>}
          {canAdd.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <select className="text-xs border border-gn-light rounded bg-gn-surface px-1 py-0.5" value={adding} onChange={(e) => setAdding(e.target.value)}>
                <option value="">{t('access.add_role')}</option>
                {canAdd.map((r) => <option key={r.id} value={r.id}>{roleName(r.key, r.name)}</option>)}
              </select>
              <button type="button" disabled={!adding || busy} title={t('access.add_role')}
                      onClick={() => { onAct('assign', adding); setAdding(''); }}><Plus size={14} /></button>
            </span>
          )}
        </div>
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        {m.state === 'INVITED' && (
          <button type="button" className={`${button} mr-1`} disabled={busy} onClick={() => onAct('resend')}
                  title={t('access.resend_invitation')} aria-label={t('access.resend_invitation')}><RotateCw size={14} /></button>
        )}
        {m.state === 'SUSPENDED' ? (
          <button type="button" className={button} disabled={busy} onClick={() => onAct('reactivate')} title={t('access.reactivate')}><Play size={14} /></button>
        ) : (
          <button type="button" className={button} disabled={busy} onClick={() => onAct('suspend')} title={t('access.suspend')}><Pause size={14} /></button>
        )}
        <button type="button" className={`${button} ml-1`} disabled={busy} title={t('access.remove')}
                onClick={() => { if (window.confirm(t('access.remove_confirm', { name: m.displayName }))) onAct('remove'); }}>
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
};

export default CompanyUsers;
